import { create } from 'zustand';
import { trackEvent } from '../lib/analytics';

interface UIModalState {
  authModalOpen: boolean;
  // Optional contextual subtitle shown in the auth modal (e.g. "saving your
  // project needs a free account"). Null for the plain/default auth prompt.
  authModalContext: string | null;
  authModalPurchase: boolean;
  pricingModalOpen: boolean;
  pricingModalSource: string;
  openAuthModal: () => void;
  openPurchaseAuthModal: () => void;
  // Same as openAuthModal, but frames the prompt around a specific action
  // (e.g. save) with a contextual message and its own GA attribution —
  // mirrors openPricingModal's source tracking below.
  openAuthModalWithContext: (source: string, contextMessage: string) => void;
  openPricingModal: (source?: string) => void;
  closeAuthModal: () => void;
  closePricingModal: () => void;
}

// Paywall dwell tracking. 85% of paywall opens never reach the CTA
// (1,633 opens → 244 clicks over 60 days), and pricing_modal_closed carried
// only page_path, so "dismissed it reflexively" and "read it and declined"
// were indistinguishable — and they call for opposite fixes (change the
// trigger vs. change the offer). Module-level rather than store state: these
// are write-once-per-open values that must not re-render the modal.
let paywallOpenedAt: number | null = null;
let paywallOpenedSource = '';

function dwellBucket(seconds: number) {
  if (seconds < 3) return 'instant';
  if (seconds < 10) return 'short';
  if (seconds < 60) return 'read';
  return 'long';
}

export const useUIModalStore = create<UIModalState>((set) => ({
  authModalOpen: false,
  authModalContext: null,
  authModalPurchase: false,
  pricingModalOpen: false,
  pricingModalSource: 'pricing_modal',
  openAuthModal: () => {
    if (typeof window !== 'undefined') {
      window.gtag?.('event', 'auth_modal_opened');
    }
    set({ authModalOpen: true, authModalContext: null, authModalPurchase: false });
  },
  openPurchaseAuthModal: () => {
    trackEvent('checkout_signin_prompt', {});
    set({ authModalOpen: true, authModalContext: null, authModalPurchase: true });
  },
  openAuthModalWithContext: (source, contextMessage) => {
    if (typeof window !== 'undefined') {
      window.gtag?.('event', 'auth_modal_opened', { source });
      trackEvent('signup_prompt_shown', { source });
    }
    set({ authModalOpen: true, authModalContext: contextMessage, authModalPurchase: false });
  },
  openPricingModal: (source = 'pricing_modal') => {
    if (typeof window !== 'undefined') {
      paywallOpenedAt = Date.now();
      paywallOpenedSource = source;
      window.gtag?.('event', 'pricing_modal_opened', {
        source,
        page_path: window.location.pathname,
      });
      trackEvent('paywall_view', {
        source,
        page_path: window.location.pathname,
      });
    }
    set({ pricingModalOpen: true, pricingModalSource: source });
  },
  closeAuthModal: () => set({ authModalOpen: false, authModalContext: null, authModalPurchase: false }),
  closePricingModal: () => {
    if (typeof window !== 'undefined') {
      const seconds = paywallOpenedAt ? Math.round((Date.now() - paywallOpenedAt) / 1000) : null;
      const bucket = seconds !== null ? dwellBucket(seconds) : null;
      const params = {
        page_path: window.location.pathname,
        ...(paywallOpenedSource ? { source: paywallOpenedSource } : {}),
        ...(seconds !== null ? { seconds_open: seconds, dwell: bucket } : {}),
      };
      trackEvent('pricing_modal_closed', params);
      // `dwell` is not a registered GA4 custom dimension, and registering one
      // needs Admin access this side does not have — so the bucket also rides
      // in the event NAME, which is always queryable. Same suffix convention
      // as tool_start_<tool> / pricing_intent_<tool>.
      if (bucket) trackEvent(`pricing_modal_closed_${bucket}`, params);
      paywallOpenedAt = null;
      paywallOpenedSource = '';
    }
    set({ pricingModalOpen: false });
  },
}));

// Dev-only: expose the store for browser-automation E2E (paywall triggers).
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  (window as unknown as { __UI_MODAL__?: typeof useUIModalStore }).__UI_MODAL__ =
    useUIModalStore;
}
